import React from 'react';
import Icon, { CheckSquareOutlined } from '@ant-design/icons';

import { ScreenProps } from '../ScreenBase';
import NextButton from '../NextButton';

import { ReactComponent as RadioSvg } from '../../../../assets/icons/radio.svg';

import styles from './choice.module.css';
import commonStyles from '../screenbase.module.css';
import { Option } from '../QuestionsList';
import { Input, InputRef, message } from 'antd';

interface ChoiceState {
  selectedOptions: Array<number>;
  otherSelected: boolean;
  text: string;
  meta?: string;
}

class Choice extends React.Component<ScreenProps, ChoiceState> {
  inputRef: React.RefObject<InputRef>;

  state: ChoiceState = {
    selectedOptions: [],
    otherSelected: false,
    text: '',
  };

  constructor(props: ScreenProps) {
    super(props);
    this.inputRef = React.createRef();
  }

  hasMaxOptions = () => {
    const { selectedOptions, otherSelected } = this.state;
    const {
      data: { extended_props },
    } = this.props;
    const { validate_count } = extended_props || {};

    const totalCount = selectedOptions.length + (otherSelected ? 1 : 0);

    return (
      validate_count &&
      ['max', 'exact'].indexOf(validate_count.type) >= 0 &&
      totalCount >= validate_count.count
    );
  };

  toggleOption = (opt: Option) => {
    const {
      data: { subtype },
      locked,
    } = this.props;

    var func = () => {};

    if (locked) return;

    let opts, meta;
    if (subtype === 'multiple') {
      opts = [...this.state.selectedOptions];

      const idx = opts.indexOf(opt.id);
      if (idx >= 0) {
        opts.splice(idx, 1);
      } else {
        if (this.hasMaxOptions()) return;
        opts.push(opt.id);
      }
    } else {
      opts = [opt.id];
      meta = opt.text;
    }

    this.setState({ selectedOptions: opts, meta }, func);
  };

  onSubmit = () => {
    if (this.validateField()) {
      const {
        data: { extended_props },
        onSubmit,
      } = this.props;
      const { redirect_map } = extended_props || {};
      const { selectedOptions, otherSelected, text, meta } = this.state;

      if (redirect_map) {
        for (let key in redirect_map) {
          if (selectedOptions.indexOf(Number(key)) >= 0) {
            window.open(redirect_map[key]);
          }
        }
      }

      onSubmit({
        text: otherSelected ? text : undefined,
        options: selectedOptions.length > 0 ? selectedOptions : undefined,
        meta,
      });
    } else {
      const {
        data: { extended_props },
      } = this.props;
      const { other_label, validation_message } = extended_props || {};
      message.warning(
        validation_message ??
          `Debes escribir algo en la opción ${other_label || '¿Otro?'}`
      );
    }
  };

  renderOption = (opt: Option) => {
    const {
      replaceTokens,
      data: { subtype },
    } = this.props;
    const selected = this.state.selectedOptions.indexOf(opt.id) >= 0;

    return (
      <div
        key={opt.id}
        className={`${styles.option} ${selected ? styles.selected : ''}`}
        onClick={() => this.toggleOption(opt)}
      >
        {subtype === 'multiple' ? (
          <CheckSquareOutlined />
        ) : (
          <Icon component={RadioSvg} />
        )}
        <span>{replaceTokens(opt.text)}</span>
      </div>
    );
  };

  validateField = () => {
    return !this.state.otherSelected || this.state.text.length > 0;
  };

  toggleOtherOption = () => {
    const { otherSelected, text } = this.state;
    const newOtherSelected = !otherSelected;

    if (newOtherSelected && this.hasMaxOptions()) return;

    this.setState(
      { otherSelected: newOtherSelected, text: !newOtherSelected ? '' : text },
      () => this.state.otherSelected && this.inputRef.current?.focus()
    );
  };

  canContinue = () => {
    const {
      data: { extended_props, required },
      locked,
    } = this.props;
    const { validate_count } = extended_props || {};
    const { selectedOptions, otherSelected } = this.state;

    if (locked) return false;
    const count = (selectedOptions || []).length + (otherSelected ? 1 : 0);

    if (validate_count) {
      switch (validate_count.type) {
        case 'min':
          return count >= validate_count.count;
        case 'max':
          return count <= validate_count.count;
        case 'exact':
          return count === validate_count.count;
      }
    } else {
      if (!required || count > 0) return true;
    }

    return false;
  };

  renderOpenOption = () => {
    const {
      data: { subtype, extended_props },
    } = this.props;
    const { other_label, input_placeholder } = extended_props || {};

    const selected = this.state.otherSelected;
    const isValid = this.validateField();
    return (
      <div
        key={-1}
        className={`${styles.option} ${styles.optionOther} ${
          selected ? styles.selected : ''
        }`}
        onClick={this.toggleOtherOption}
      >
        {subtype === 'multiple' ? (
          <CheckSquareOutlined />
        ) : (
          <Icon component={RadioSvg} />
        )}
        <span>{other_label || '¿Otro?'}</span>
        {selected && (
          <Input
            ref={this.inputRef}
            className={`${styles.input} ${!isValid ? styles.inputError : ''}`}
            onClick={(e) => e.stopPropagation()}
            placeholder={input_placeholder ?? '¿Cuál?'}
            value={this.state.text}
            onChange={(e) => this.setState({ text: e.target.value })}
          />
        )}
      </div>
    );
  };

  render() {
    const {
      data: { options, subtype, open_option, extended_props },
    } = this.props;
    const { hint, button_text } = extended_props || {};

    return (
      <div className={styles.choice}>
        {(subtype === 'multiple' || hint) && (
          <div className={commonStyles.note}>
            {hint || 'Puedes seleccionar varias opciones'}
          </div>
        )}
        <div className={styles.optionsContainer}>
          {(options || []).map((opt) => this.renderOption(opt))}
          {open_option && this.renderOpenOption()}
        </div>
        <NextButton
          visible={this.canContinue()}
          onClick={this.onSubmit}
          text={button_text}
        />
      </div>
    );
  }
}

export default Choice;
